import { createFileRoute } from "@tanstack/react-router";
import ServiceSeoGeo from "@/pages/ServiceSeoGeo";
import {
  buildPageHead,
  breadcrumbSchema,
  serviceSchema,
  faqPageSchema,
  videoObjectSchema,
} from "@/lib/seo";

const faqs = [
  {
    question: "What is the difference between SEO and GEO?",
    answer:
      "SEO optimizes your pages to rank in classic search engines like Google and Bing. GEO (Generative Engine Optimization) engineers your content so AI engines like ChatGPT, Gemini, and Claude retrieve and cite your brand as the answer. I architect both together, because the same entity, schema, and citable-chunk layers that rank you in search are the layers that get you cited by AI.",
  },
  {
    question: "How do you get my brand cited by ChatGPT, Gemini, and Claude?",
    answer:
      "I engineer three layers: a consolidated author and brand entity with sameAs corroboration so LLMs recognize one canonical source, clean structured data (Article, FAQPage, Person schema) so the content parses cleanly, and citable chunks (answer-first leads, definition boxes, standalone Q&A) engineered to be lifted verbatim. LLMs cite content that is structured, corroborated, and easy to retrieve — I build all three.",
  },
  {
    question: "How is GEO different from AEO?",
    answer:
      "AEO (Answer Engine Optimization) focuses on winning answer-box and featured-snippet placements in search. GEO focuses on being cited inside generative AI responses. The two overlap heavily — both reward answer-first structure and clean schema — so I architect them as one system rather than two separate efforts.",
  },
  {
    question: "Do you fix a content stack another freelancer built?",
    answer:
      "Yes. I inherit and rebuild broken SEO with a documented audit first, mapping every page, schema tag, internal link, and entity signal before any changes, so nothing breaks during the transition.",
  },
  {
    question: "How much does an SEO & GEO architect cost?",
    answer:
      "Engagements are scoped per project or retainer. A senior visibility architect delivers the full architecture at a fraction of a US agency rate. Book a free visibility audit call to receive a custom scope and quote.",
  },
  {
    question: "How long until I see AI citations and ranking gains?",
    answer:
      "Citation and ranking gains typically surface within 4 to 12 weeks as crawlers re-index the rebuilt schema, entity, and content architecture. The compounding return comes from the internal link graph and entity consolidation, which strengthen every new piece of content you publish afterward.",
  },
  {
    question: "Do you build schema and structured data by hand?",
    answer:
      "Yes. I hand-build and validate Article, FAQPage, Person, BreadcrumbList, and Service schema rather than relying on plugin defaults, so the structured data parses cleanly into rich results and AI citations.",
  },
  {
    question: "Can you consolidate my author or brand entity?",
    answer:
      "Yes. I consolidate fragmented author and brand profiles under one canonical @id with sameAs corroboration across LinkedIn, Crunchbase, Wikidata, and your owned properties, so Google's Knowledge Graph and LLMs recognize you as the single canonical source.",
  },
  {
    question: "How do we get started without disrupting live rankings?",
    answer:
      "Every engagement begins with an audit and staging blueprint. We validate all schema, entity, and content changes in a sandbox environment before rollout, ensuring your active rankings run 100% uninterrupted.",
  },
];

export const Route = createFileRoute("/seo-geo-architect")({
  head: () =>
    buildPageHead({
      title: "SEO & GEO Architect | Rank & Get Cited by AI | Marjohn Robillo",
      description:
        "Hire an SEO & GEO architect. Generative Engine Optimization, answer-engine citation engineering, entity consolidation, schema, and internal link graphs that rank your brand and get it cited by LLMs.",
      path: "/seo-geo-architect",
      keywords:
        "SEO architect, GEO, generative engine optimization, AEO, answer engine optimization, AI citation, ChatGPT citation, Gemini citation, Claude citation, LLM citation, entity consolidation, schema structured data, internal link graph, search visibility, Marjohn Robillo",
      schema: [
        serviceSchema({
          name: "SEO & GEO Visibility Architecture",
          description:
            "Generative Engine Optimization, answer-engine citation engineering, entity consolidation, schema, and internal link graphs that rank pages in Google and Bing and get brands cited by ChatGPT, Gemini, and Claude.",
          path: "/seo-geo-architect",
          serviceType: [
            "Search Engine Optimization (SEO)",
            "Generative Engine Optimization (GEO)",
            "Answer Engine Optimization (AEO)",
            "AI Citation Engineering",
            "Entity & Knowledge Graph Consolidation",
            "Schema & Structured Data Engineering",
            "Internal Link Graph Architecture",
            "Content Cluster Topology",
          ],
        }),
        breadcrumbSchema([
          { name: "Home", path: "" },
          {
            name: "SEO & GEO Architect",
            path: "/seo-geo-architect",
          },
        ]),
        faqPageSchema(faqs),
        videoObjectSchema({
          name: "SEO & GEO: How to Get Cited by ChatGPT, Gemini & Claude",
          description:
            "Marjohn Robillo explains how Generative Engine Optimization, entity consolidation, and citable-chunk architecture get a brand ranked in Google and Bing and cited by ChatGPT, Gemini, and Claude.",
        }),
      ],
    }),
  component: ServiceSeoGeo,
});
